import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AwsCredentialProvider,
  FaceLivenessDetectorCore,
} from "@aws-amplify/ui-react-liveness";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import "@aws-amplify/ui-react/styles.css";
import { configureAmplify } from "./amplify";
import { livenessConfig } from "./regionConfig";
import "./App.css";
import { ThemeProvider, createTheme } from "@aws-amplify/ui-react";

configureAmplify();

type SessionResponse = { sessionId: string; region: string };

const theme = createTheme({
  name: "convooooo",
  tokens: {
    colors: {
      brand: {
        primary: {
          80: { value: "#FFFFFF" },
        },
      },
    },
    radii: {
      medium: { value: "12px" },
      large: { value: "16px" },
    },
    components: {
      button: {
        primary: {
          backgroundColor: { value: "{colors.brand.primary.80}" },
        },
      },
    },
  },
});

function getTokenFromUrl(): string | null {
  const url = new URL(window.location.href);
  return url.searchParams.get("token");
}

function postToRN(payload: any) {
  (window as any).ReactNativeWebView?.postMessage(JSON.stringify(payload));
}

function toErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (err && typeof err === "object") {
    const anyErr = err as any;
    const parts: string[] = [];

    if (typeof anyErr.name === "string") parts.push(`name=${anyErr.name}`);
    if (typeof anyErr.code === "string") parts.push(`code=${anyErr.code}`);
    if (typeof anyErr.message === "string")
      parts.push(`message=${anyErr.message}`);
    if (typeof anyErr.error === "string") parts.push(`error=${anyErr.error}`);
    if (anyErr.$metadata?.httpStatusCode != null) {
      parts.push(`httpStatus=${anyErr.$metadata.httpStatusCode}`);
    }

    if (parts.length) return parts.join(" | ");
  }
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

function getOrCreateTraceId(): string {
  const anyWin = window as any;
  if (typeof anyWin.__cvTraceId === "string" && anyWin.__cvTraceId.length) {
    return anyWin.__cvTraceId;
  }

  const traceId =
    (typeof crypto !== "undefined" && "randomUUID" in crypto
      ? (crypto as any).randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`) ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  anyWin.__cvTraceId = traceId;
  return traceId;
}

function log(
  traceId: string,
  severity: "DEBUG" | "INFO" | "WARNING" | "ERROR",
  event: string,
  fields?: Record<string, unknown>,
) {
  const payload = {
    severity,
    event,
    traceId,
    ts: new Date().toISOString(),
    ...fields,
  };

  const line = JSON.stringify(payload);
  if (severity === "ERROR") console.error(line);
  else if (severity === "WARNING") console.warn(line);
  else console.log(line);
}

export default function App() {
  const token = useMemo(() => getTokenFromUrl(), []);
  const traceId = useMemo(() => getOrCreateTraceId(), []);

  // Which region's backend and AWS account this page operates against, taken
  // from the `region` query param the mobile app appends. One deployment serves
  // both regions — see src/regionConfig.ts.
  const appRegion = livenessConfig.region;

  // Check the active region's config was baked into this bundle. A missing
  // triplet means the build args for `appRegion` were not passed, which would
  // otherwise surface downstream as an opaque 401 or an AWS auth failure.
  if (
    !livenessConfig.awsRegion ||
    !livenessConfig.identityPoolId ||
    !livenessConfig.apiBase
  ) {
    const detail =
      `Liveness web is not configured for region ${appRegion} ` +
      `(missing: ${[
        !livenessConfig.awsRegion && "awsRegion",
        !livenessConfig.identityPoolId && "identityPoolId",
        !livenessConfig.apiBase && "apiBase",
      ]
        .filter(Boolean)
        .join(", ")}). The build args for that region were not passed.`;
    log(traceId, "ERROR", "config_missing_env", {
      appRegion,
      hasAwsRegion: Boolean(livenessConfig.awsRegion),
      hasIdentityPoolId: Boolean(livenessConfig.identityPoolId),
      hasApiBase: Boolean(livenessConfig.apiBase),
      href: window.location.href,
    });
    // Tell the native side BEFORE throwing. This throw happens during render,
    // outside the session effect's try/catch, so without this the WebView just
    // white-screens and the app waits forever on a message that never comes —
    // a misconfigured deploy looks like a hang rather than a config error.
    postToRN({ type: "LIVENESS_ERROR", message: detail });
    throw new Error(detail);
  }

  const [session, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const region = livenessConfig.awsRegion;
  const identityPoolId = livenessConfig.identityPoolId;
  const apiBase = livenessConfig.apiBase;

  useEffect(() => {
    let apiOrigin = "";
    try {
      apiOrigin = new URL(apiBase).origin;
    } catch {
      apiOrigin = apiBase;
    }

    log(traceId, "INFO", "app_init", {
      href: window.location.href,
      hasToken: Boolean(token),
      appRegion,
      apiOrigin,
      awsRegion: region,
      identityPoolIdSuffix: identityPoolId.slice(-6),
      userAgent: navigator.userAgent,
    });

    const onError = (ev: ErrorEvent) => {
      log(traceId, "ERROR", "window_error", {
        message: ev.message,
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
      });
    };

    const onUnhandledRejection = (ev: PromiseRejectionEvent) => {
      log(traceId, "ERROR", "unhandled_rejection", {
        reason: toErrorMessage(ev.reason),
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [apiBase, appRegion, identityPoolId, region, token, traceId]);

  const credentialProvider = useCallback<AwsCredentialProvider>(async () => {
    const t0 = performance.now();
    log(traceId, "DEBUG", "aws_creds_resolve_start", {
      awsRegion: region,
      identityPoolIdSuffix: identityPoolId.slice(-6),
    });

    try {
      const provider = fromCognitoIdentityPool({
        client: new CognitoIdentityClient({ region }),
        identityPoolId,
      });

      const creds = await provider();
      if (!creds.accessKeyId || !creds.secretAccessKey) {
        throw new Error("Failed to resolve guest AWS credentials");
      }

      log(traceId, "DEBUG", "aws_creds_resolve_ok", {
        ms: Math.round(performance.now() - t0),
        hasSessionToken: Boolean(creds.sessionToken),
        expiration: creds.expiration
          ? new Date(creds.expiration).toISOString()
          : null,
      });

      return {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
        expiration: creds.expiration,
      };
    } catch (e) {
      log(traceId, "ERROR", "aws_creds_resolve_error", {
        ms: Math.round(performance.now() - t0),
        error: toErrorMessage(e),
      });
      throw e;
    }
  }, [identityPoolId, region, traceId]);

  const displayText = useMemo(
    () =>
      ({
        startScreenBeginCheckText: "Start verification",
        cancelLivenessCheckText: "Cancel",
        waitingCameraPermissionText: "Allow camera access to continue.",
        retryCameraPermissionsText: "Retry",
        cameraNotFoundHeadingText: "Camera not available",
        cameraNotFoundMessageText:
          "We couldn’t access your camera. Check permissions and close other apps using the camera.",
        cameraMinSpecificationsHeadingText: "Camera not supported",
        cameraMinSpecificationsMessageText:
          "Your camera must support at least 320×240 resolution and 15 fps.",

        hintCenterFaceText: "Center your face",
        hintMoveFaceFrontOfCameraText: "Move into view",
        hintTooCloseText: "Move back",
        hintTooFarText: "Move closer",
        hintHoldFaceForFreshnessText: "Hold still",
        hintConnectingText: "Connecting…",
        hintVerifyingText: "Verifying…",
        hintCheckCompleteText: "Done",
        hintTooManyFacesText: "Only one face should be visible",
        hintCanNotIdentifyText: "Center your face",
        hintIlluminationTooBrightText: "Move to a dimmer area",
        hintIlluminationTooDarkText: "Move to a brighter area",
        hintIlluminationNormalText: "Lighting looks good",

        goodFitCaptionText: "Good fit",
        tooFarCaptionText: "Too far",

        errorLabelText: "Verification error",
        tryAgainText: "Try again",
        landscapeHeaderText: "Rotate your phone",
        landscapeMessageText: "Use portrait (vertical) orientation.",
        portraitMessageText:
          "Keep your phone upright (portrait) during the whole check.",

        connectionTimeoutHeaderText: "Connection timed out",
        connectionTimeoutMessageText: "Please try again.",
        timeoutHeaderText: "Timed out",
        timeoutMessageText:
          "We couldn’t get a good reading in time. Try again and keep your face inside the oval.",
        multipleFacesHeaderText: "Multiple faces detected",
        multipleFacesMessageText:
          "Make sure only your face is visible in the camera.",
        serverHeaderText: "Server issue",
        serverMessageText:
          "We couldn’t complete verification right now. Please try again.",
        clientHeaderText: "App issue",
        clientMessageText: "Please close and reopen verification.",

        recordingIndicatorText: "REC",

        photosensitivityWarningHeadingText: "",
        photosensitivityWarningBodyText: "",
        photosensitivityWarningInfoText: "",
        photosensitivityWarningLabelText: "",
      }) as any,
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        if (!token) throw new Error("Missing token in URL");

        const url = `${apiBase}/api/liveness/session`;
        const t0 = performance.now();
        log(traceId, "INFO", "session_fetch_start", {
          url,
          method: "POST",
          appRegion,
        });

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        log(traceId, "INFO", "session_fetch_response", {
          url,
          status: res.status,
          ok: res.ok,
          ms: Math.round(performance.now() - t0),
        });

        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          log(traceId, "ERROR", "session_fetch_non_2xx", {
            url,
            status: res.status,
            bodyPreview: errText.slice(0, 300),
          });
          throw new Error(`Session error: ${res.status}`);
        }

        const data = (await res.json()) as SessionResponse;

        log(traceId, "INFO", "session_fetch_ok", {
          sessionId: data?.sessionId,
          sessionRegion: data?.region,
        });

        // The detector streams to `data.region` (where the backend created the
        // session) using guest credentials minted in `region` (this bundle's
        // pool for appRegion). Same-AWS-account credentials are usable across
        // regions, so a mismatch is not necessarily fatal — but it means this
        // region's backend and pool disagree, which is a misconfiguration and
        // *is* fatal across accounts. Warn rather than throw so a working setup
        // is not broken, while the real cause is named if the stream fails.
        if (data?.region && data.region !== region) {
          log(traceId, "WARNING", "session_region_mismatch", {
            appRegion,
            sessionRegion: data.region,
            credentialRegion: region,
          });
        }

        setSession(data);
      } catch (e: any) {
        postToRN({ type: "LIVENESS_ERROR", message: toErrorMessage(e) });
        log(traceId, "ERROR", "session_fetch_error", {
          error: toErrorMessage(e),
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, appRegion, region, token, traceId]);

  if (loading) {
    return <div className="cv-screen"></div>;
  }

  if (!session) {
    return (
      <div className="cv-screen">
        <div className="cv-center">
          <div className="cv-title">Couldn’t start verification</div>
          <div className="cv-subtitle">Please try again.</div>
          <button className="cv-btn" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="cv-screen cv-start">
        <div className="cv-startContent">
          <div className="cv-subtitle-1">
            We’ll quickly check that you’re a real person.
          </div>
          <div className="cv-subtitle">
            This helps keep conversations
            <br />
            <span className="cv-emphasis">genuine and safe.</span>
          </div>
          <div className="cv-subtitle-2">
            Center your face and use good lighting.
          </div>

          <button
            className="cv-btn cv-btnPrimary"
            onClick={() => {
              log(traceId, "INFO", "ui_start_clicked", {});
              setStarted(true);
            }}
          >
            Start verification
          </button>

          <div className="cv-footnote">We don’t save your video or photos.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cv-screen">
      <div className="cv-body">
        <div className="cv-detectorCard">
          <ThemeProvider theme={theme} colorMode="dark">
            <FaceLivenessDetectorCore
              sessionId={session.sessionId}
              region={session.region}
              config={{ credentialProvider }}
              disableStartScreen={true}
              displayText={displayText}
              onAnalysisComplete={async () => {
                postToRN({
                  type: "LIVENESS_DONE",
                  sessionId: session.sessionId,
                });
                log(traceId, "INFO", "liveness_complete", {
                  sessionId: session.sessionId,
                });
              }}
              onError={(err) => {
                postToRN({
                  type: "LIVENESS_ERROR",
                  message: toErrorMessage(err),
                });
                log(traceId, "ERROR", "liveness_error", {
                  sessionId: session.sessionId,
                  error: toErrorMessage(err),
                });
              }}
            />
          </ThemeProvider>
        </div>

        <div className="cv-footnote">
          By continuing, you agree to be recorded for liveness verification.
        </div>
      </div>
    </div>
  );
}
